import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import NavItem from './NavItem';
import styles from './index.module.scss';

export interface NavCarouselItem {
  title: React.ReactNode;
  description: string;
  image: string;
  link?: string;
  alt?: string;
}

export interface NavCarouselProps {
  items: NavCarouselItem[];
  interval?: number;
  className?: string;
}

export default function NavCarousel({
  items,
  interval = 5000,
  className,
}: NavCarouselProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      setInView(entry.isIntersecting);
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (paused || !inView || items.length <= 1) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setActiveIndex((i) => (i + 1) % items.length);
    }, interval);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [paused, inView, interval, items.length]);

  const handleEnter = (index: number) => {
    setPaused(true);
    setActiveIndex(index);
  };

  const handleLeave = () => {
    setPaused(false);
  };

  const handleClick = (index: number) => {
    setActiveIndex(index);
  };

  const rootClass = className ? `${styles.root} ${className}` : styles.root;
  const activeItem = items[activeIndex];
  const visualLabel =
    activeItem.alt || (typeof activeItem.title === 'string' ? `查看${activeItem.title}` : '查看详情');

  const visuals = items.map((item, index) => (
    <img
      key={index}
      src={item.image}
      alt={item.alt ?? ''}
      className={index === activeIndex ? styles.visible : styles.hidden}
      loading="lazy"
    />
  ));

  const visualHoverPause = {
    onMouseEnter: () => setPaused(true),
    onMouseLeave: () => setPaused(false),
  };

  return (
    <div ref={rootRef} className={rootClass}>
      <div className={styles.nav} onMouseLeave={handleLeave}>
        {items.map((item, index) => (
          <NavItem
            key={index}
            title={item.title}
            description={item.description}
            link={item.link}
            active={index === activeIndex}
            onMouseEnter={() => handleEnter(index)}
            onClick={() => handleClick(index)}
          />
        ))}
      </div>
      {activeItem.link ? (
        <Link
          className={styles.visual}
          to={activeItem.link}
          aria-label={visualLabel}
          {...visualHoverPause}
        >
          {visuals}
        </Link>
      ) : (
        <div className={styles.visual}>{visuals}</div>
      )}
    </div>
  );
}
